/**
 * Patient portal — the simplest possible view: your own studies, plain
 * language summary, ability to share with a doctor via a time-limited
 * token.
 *
 * Backend enforces MRN scoping via X-VaultPod-Claims (vaultpod_mrn
 * from the JWT); this page just renders whatever is returned.
 */
import { createSignal, For, Show } from "solid-js";
import { createQuery } from "@tanstack/solid-query";
import { A } from "@solidjs/router";
import { listStudies, type Study } from "../api/client";
import { auth } from "../auth/store";

export function PatientPortalPage() {
  const [shareOpen, setShareOpen] = createSignal<string | null>(null);

  const studies = createQuery(() => ({
    queryKey: ["studies", "portal"],
    queryFn:  () => listStudies({}),
  }));

  // Patient role retired in the 4-role matrix; the portal is no
  // longer a first-class surface. The MRN claim is gone — this page
  // only mounts at all because the route still exists. Show "—".
  const mrn  = () => "—";
  const name = () => auth()?.claims.name ?? auth()?.claims.preferred_username ?? "Patient";

  return (
    <div class="flex h-full flex-col">
      <div class="border-b border-ink-300 bg-gradient-to-b from-ink-100 to-ink-50 p-6">
        <div class="mx-auto max-w-3xl">
          <div class="text-xs uppercase tracking-wider text-ink-700">VaultPod Patient Portal</div>
          <h1 class="mt-1 text-2xl font-semibold text-white">Hi, {name()}</h1>
          <p class="mt-2 text-sm text-ink-700">
            Here are all the imaging studies we have on file for you
            (MRN <span class="font-mono text-ink-900">{mrn()}</span>).
            Open any study to view the images and the radiologist's
            report. Need your care team to see something? Use
            <em> Share with a doctor </em> — they'll get a secure link
            that expires in 24 hours.
          </p>
        </div>
      </div>

      <div class="mx-auto flex w-full max-w-3xl flex-1 flex-col gap-3 overflow-auto p-4">
        <Show when={studies.isLoading}><div class="p-4 text-center text-ink-700">loading…</div></Show>
        <Show when={studies.data && studies.data.length === 0}>
          <div class="rounded-lg border border-ink-300 bg-ink-100 p-8 text-center">
            <div class="text-base">No studies on file.</div>
            <div class="mt-2 text-sm text-ink-700">
              If you recently had imaging done and it isn't visible
              here, please contact your clinic — it can take up to
              a few hours for results to arrive in the portal.
            </div>
          </div>
        </Show>

        <For each={studies.data ?? []}>
          {(s: Study) => (
            <div class="rounded-lg border border-ink-300 bg-ink-100 p-4 shadow-panel">
              <div class="flex items-start justify-between gap-3">
                <div>
                  <div class="font-semibold text-white">
                    {s.description || "Imaging study"}
                  </div>
                  <div class="text-xs text-ink-700">
                    {fmtDate(s.studyDate)} · {s.modality}
                  </div>
                </div>
                <Show when={s.reportSigned}
                      fallback={
                        <span class="rounded-full border border-warn/50 bg-warn/10 px-3 py-1 text-[11px] text-warn">
                          Report pending
                        </span>
                      }>
                  <span class="rounded-full border border-brand/50 bg-brand/10 px-3 py-1 text-[11px] text-brand">
                    Report ready
                  </span>
                </Show>
              </div>

              <p class="mt-3 text-sm leading-relaxed text-ink-800">
                {plainSummary(s)}
              </p>

              <div class="mt-4 flex flex-wrap items-center gap-2">
                <A href={`/study/${s.studyUid}`} class="btn-primary text-sm">
                  View images
                </A>
                <button type="button" class="btn-ghost text-sm"
                        onClick={() => setShareOpen(s.studyUid)}>
                  Share with a doctor
                </button>
              </div>
            </div>
          )}
        </For>
      </div>

      <Show when={shareOpen()}>
        <ShareModal studyUid={shareOpen()!} onClose={() => setShareOpen(null)} />
      </Show>
    </div>
  );
}

function plainSummary(s: Study): string {
  // Deliberately simple — the real summary comes from the radiology
  // report NLP pipeline. For now we give a friendly placeholder based
  // on modality so the UI feels warm even without a report.
  switch (s.modality) {
    case "CT": return "A CT scan — an X-ray based 3D image, useful for looking inside bones, lungs, and the abdomen.";
    case "MR": return "An MRI scan — uses magnets to get detailed pictures of soft tissues like the brain, heart, and joints.";
    case "US": return "An ultrasound — uses safe sound waves to look at organs and babies during pregnancy.";
    case "CR":
    case "DR":
    case "DX": return "A standard X-ray — the quick 2D scan most people know, good for bones and lungs.";
    case "MG": return "A mammogram — an X-ray of the breast used for screening and diagnosis.";
    case "NM":
    case "PT": return "A nuclear medicine scan — a tiny amount of tracer shows how tissues are working.";
    case "XC":
    case "ES": return "An endoscopy video — pictures from a tiny camera inside your body, used by your doctor to check an organ.";
    default:   return "An imaging study. Your doctor can explain what it shows.";
  }
}

function ShareModal(p: { studyUid: string; onClose: () => void }) {
  const [copied, setCopied] = createSignal(false);
  const share = `${window.location.origin}/shared/${p.studyUid}?token=vp-demo-not-wired`;

  function copy() {
    navigator.clipboard.writeText(share).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }

  return (
    <div class="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4" onClick={p.onClose}>
      <div class="w-full max-w-md rounded-lg border border-ink-300 bg-ink-100 p-6 shadow-panel"
           onClick={(e) => e.stopPropagation()}>
        <div class="text-lg font-semibold">Share with a doctor</div>
        <p class="mt-2 text-sm text-ink-700">
          Send this link to any care provider. It expires automatically
          24 hours from now and shows only this one study — no other
          records.
        </p>
        <div class="mt-4 rounded border border-ink-300 bg-ink-200 p-2 font-mono text-[11px] break-all">
          {share}
        </div>
        <div class="mt-4 flex items-center justify-end gap-2">
          <button class="btn-ghost" onClick={p.onClose}>Close</button>
          <button class="btn-primary" onClick={copy}>
            {copied() ? "Copied!" : "Copy link"}
          </button>
        </div>
        <p class="mt-4 text-[11px] text-ink-700">
          Tokens are signed, time-limited, and audited. If someone you
          don't recognise views the link, we notify you by email.
        </p>
      </div>
    </div>
  );
}

function fmtDate(d: string): string {
  if (!d || d.length !== 8) return d;
  return `${d.slice(0, 4)}-${d.slice(4, 6)}-${d.slice(6, 8)}`;
}
